import { cn, useIntersectionObserver } from '@nexelem/utils';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { PropsWithChildren, ReactNode, forwardRef } from 'react';
import { Button, ButtonProps } from '../Button';
import { Checkbox } from '../Checkbox';
import { TextField, TextFieldProps } from '../TextField';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faSquareX } from '@fortawesome/pro-light-svg-icons';
import { t } from 'i18next';
import { Skeleton } from '../Skeleton';

const DropdownMenuContent = forwardRef<HTMLDivElement, DropdownMenuPrimitive.DropdownMenuContentProps>(
  ({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        className={cn(
          'z-1500 m-1 flex flex-col items-stretch justify-stretch overflow-hidden rounded-xs bg-surface-primary shadow-small',
          // animations
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          className,
        )}
        {...props}
        ref={ref}
      />
    </DropdownMenuPrimitive.Portal>
  ),
);

DropdownMenuContent.displayName = 'DropdownMenuContent';

const DropdownMenuButton = forwardRef<HTMLDivElement, DropdownMenuPrimitive.DropdownMenuItemProps & ButtonProps>(
  ({ className, children, ...props }, ref) => (
    <DropdownMenuPrimitive.Item asChild ref={ref}>
      <Button className={cn('justify-start rounded-none disabled:border-none', className)} variant="plain" {...props}>
        {children}
      </Button>
    </DropdownMenuPrimitive.Item>
  ),
);

DropdownMenuButton.displayName = 'DropdownMenuItem';

const DropdownTrigger = forwardRef<HTMLButtonElement, Omit<DropdownMenuPrimitive.DropdownMenuTriggerProps, 'asChild'>>(
  ({ children, ...props }, ref) => (
    <DropdownMenuPrimitive.Trigger asChild ref={ref} {...props}>
      {children}
    </DropdownMenuPrimitive.Trigger>
  ),
);

DropdownTrigger.displayName = 'DropdownTrigger';

export const DropdownCheckbox = forwardRef<HTMLDivElement, DropdownMenuPrimitive.DropdownMenuCheckboxItemProps>(
  ({ className, children, checked, ...props }, ref) => (
    <DropdownMenuPrimitive.CheckboxItem
      checked={checked}
      ref={ref}
      className={cn(
        'flex cursor-pointer items-center gap-2 px-4 py-3 text-s16 hover:bg-surface-focus-lighter/50 focus:bg-surface-focus-lighter/50 focus-visible:outline-none aria-[disabled]:cursor-not-allowed aria-[disabled]:opacity-50 data-[state=checked]:font-semibold',
        className,
      )}
      onPointerLeave={(e) => e.preventDefault()}
      onPointerMove={(e) => e.preventDefault()}
      {...props}
    >
      <Checkbox checked={!!checked} disabled={props.disabled} />
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  ),
);

DropdownCheckbox.displayName = 'DropdownCheckbox';

type DropdownCalendarContainerProps = DropdownMenuPrimitive.DropdownMenuContentProps & {
  footer?: ReactNode;
};

const DropdownCalendarContainer = forwardRef<HTMLDivElement, DropdownCalendarContainerProps>(
  ({ className, children, footer, ...props }, ref) => (
    <DropdownMenuContent align="start" {...props} className={cn('z-50', className)} ref={ref}>
      <div className="p-3">{children}</div>
      {footer && <div className="flex border-t border-border-primary p-3">{footer}</div>}
    </DropdownMenuContent>
  ),
);

DropdownCalendarContainer.displayName = 'DropdownCalendarContainer';

const DropdownRadioItem = forwardRef<HTMLDivElement, DropdownMenuPrimitive.DropdownMenuRadioItemProps>(
  ({ className, children, ...props }, ref) => (
    <DropdownMenuPrimitive.RadioItem
      ref={ref}
      className={cn(
        'flex cursor-pointer items-center gap-2 px-4 py-3 text-s16 focus:bg-surface-focus-lighter/50 focus-visible:outline-none aria-[disabled]:cursor-not-allowed aria-[disabled]:opacity-50 data-[state=checked]:font-semibold',
        className,
      )}
      {...props}
    >
      <span className="flex h-4 w-4 items-center justify-center rounded-full border border-icon-primary">
        <DropdownMenuPrimitive.ItemIndicator>
          <span className="block h-2 w-2 rounded-full bg-icon-focus" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  ),
);

DropdownRadioItem.displayName = 'DropdownRadioItem';

const DropdownSearch = ({ value, onChange, ref }: Pick<TextFieldProps<string>, 'value' | 'onChange' | 'ref'>) => (
  <DropdownMenuPrimitive.DropdownMenuItem
    asChild
    autoFocus={true}
    className="border-b border-border-primary p-4"
    onKeyDown={(e) => e.stopPropagation()}
    onPointerLeave={(e) => e.preventDefault()}
    onPointerMove={(e) => e.preventDefault()}
    onSelect={(e) => e.preventDefault()}
  >
    <TextField
      enableClearButton
      placeholder={t('common.search') as string}
      ref={ref}
      size="small"
      StartIcon={() => <FontAwesomeIcon icon={faMagnifyingGlass} />}
      value={value}
      onChange={onChange}
      onKeyDownCapture={(e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          return;
        }
        e.stopPropagation();
      }}
    />
  </DropdownMenuPrimitive.DropdownMenuItem>
);

export type InfinityDataSource = {
  hasMore: boolean;
  onLoadMore: () => void;
  isLoadingMore: boolean;
  isLoading: boolean;
  isError: boolean;
  data: object[];
  hasClearAll: boolean;
  clearAll: () => void;
};

type DropdownSearchContainerProps = DropdownMenuPrimitive.DropdownMenuContentProps & {
  searchValue: string;
  onSearchChange: (value: string) => void;
  dataSource?: InfinityDataSource;
  hasClearAll?: boolean;
  clearAll?: () => void;
};

const DropdownSearchContainer = ({
  className,
  children,
  searchValue,
  onSearchChange,
  dataSource,
  hasClearAll,
  clearAll,
  ...props
}: PropsWithChildren<DropdownSearchContainerProps>) => {
  const { rootRef, observeRef } = useIntersectionObserver(() => dataSource?.onLoadMore());

  const resolvedHasClearAll = dataSource ? dataSource.hasClearAll : !!hasClearAll;
  const resolvedClearAll = dataSource ? dataSource.clearAll : clearAll;

  return (
    <DropdownMenuContent align="start" {...props} className={cn('z-50', className)} ref={rootRef}>
      <DropdownSearch value={searchValue} onChange={onSearchChange} />
      <div className="max-h-64 overflow-y-auto">
        {children}
        {dataSource?.data.length === 0 && !dataSource?.isLoading && !dataSource?.isError && (
          <div className="p-4 text-center text-s16 text-text-secondary">{t('common.noResultsFound')}</div>
        )}
        {dataSource?.isError && (
          <div className="p-4 text-center text-s16-sb text-text-state-error-light">{t('common.errorMessage')}</div>
        )}
        {(dataSource?.isLoadingMore || dataSource?.isLoading) && (
          <div className="flex flex-col gap-3 px-4 pb-3">
            <Skeleton className="h-8" />
            <Skeleton className="h-8" />
          </div>
        )}
        {dataSource && dataSource.hasMore && dataSource.data.length > 0 && (
          <div aria-hidden="true" className="h-1" ref={observeRef} />
        )}
      </div>
      {resolvedHasClearAll && resolvedClearAll && (
        <DropdownMenu.Button
          className="mb-1 justify-center border-0 border-t border-border-primary"
          StartIcon={<FontAwesomeIcon icon={faSquareX} />}
          onClick={resolvedClearAll}
        >
          {t('common.clearAll')}
        </DropdownMenu.Button>
      )}
    </DropdownMenuContent>
  );
};

export const DropdownMenu = {
  Root: DropdownMenuPrimitive.Root,
  Trigger: DropdownTrigger,
  Content: DropdownMenuContent,
  Button: DropdownMenuButton,
  Checkbox: DropdownCheckbox,
  RadioGroup: DropdownMenuPrimitive.RadioGroup,
  RadioItem: DropdownRadioItem,
  CalendarContainer: DropdownCalendarContainer,
  Search: DropdownSearch,
  SearchContainer: DropdownSearchContainer,
  Item: DropdownMenuPrimitive.DropdownMenuItem,
};
